/**
 * plugins/vuetify.js
 *
 * Framework documentation: https://vuetifyjs.com`
 */

// Styles
import { ICONS } from "@/libs/icons";
import "@mdi/font/css/materialdesignicons.css";
import { aliases, mdi } from "vuetify/iconsets/mdi";
import "vuetify/styles";

// Composables
import { createVuetify } from "vuetify";
import * as components from "vuetify/components";
import * as directives from "vuetify/directives";
import { VMonthPicker } from "vuetify/labs/VMonthPicker";
import { cs, en } from "vuetify/locale";

const DARK_THEME_YELLOW = "#FFB300";
const LIGHT_THEME_YELLOW = "#F0B03F";

const COUNTER_REGISTRY = "#107da6";

// SUSHI harvesting states (SushiStatusChart): five families, light and dark
// steps of one hue each, so neighbours in the stacked bar stay apart also for
// color blind users; the high contrast theme takes the light ones
const LIGHT_HARVEST_COLORS = {
  "harvest-success": "#9BD3AA",
  "harvest-partial-data": "#FFB980",
  "harvest-empty-data": "#1D7239", // same as success-text
  "harvest-unknown": "#8F5F22",
  "harvest-not-used": "#343A40",
  "harvest-error": "#F5B3B8",
  "harvest-import-failed": "#E5484D", // error (#ff5252) is too light for text
  "harvest-broken-report": "#A3261E",
  "harvest-broken": "#6D1F5E",
  "harvest-awaiting-import": "#01579B",
  "harvest-waiting": "#5AA2D6",
  "harvest-missing": "#D3D9DE", // same as outline-variant
};
const DARK_HARVEST_COLORS = {
  "harvest-success": "#2E6B3F",
  "harvest-partial-data": "#FFB980",
  // lighter than the theme's success (#4CAF50), which a color blind user
  // can't tell from the orange unknown next to it
  "harvest-empty-data": "#81C995",
  "harvest-unknown": "#C9954A",
  "harvest-not-used": "#5E676F",
  "harvest-error": "#8C3A44",
  "harvest-import-failed": "#FF6B70",
  "harvest-broken-report": "#E0533F",
  "harvest-broken": "#C77DBA",
  "harvest-awaiting-import": "#4FB3F0",
  "harvest-waiting": "#2D6E99",
  "harvest-missing": "#3A4046",
};

// used when the user has not picked a theme in their preferences: Vuetify follows
// the browser's prefers-color-scheme and resolves it to the theme named "light"
// or "dark", so those two keys must stay (users see them as Light mint / Dark blue)
export const DEFAULT_THEME = "system";

// the theme applied on the last visit, used at startup so the boot screen does not
// flash the default theme before the user's preferences arrive from the API
const THEME_STORAGE_KEY = "celus-theme";

export function rememberTheme(name) {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, name);
  } catch {
    // storage unavailable (e.g. blocked site data) - the next load starts from the default
  }
}

function rememberedTheme() {
  try {
    return localStorage.getItem(THEME_STORAGE_KEY);
  } catch {
    return null;
  }
}

// shared by all data table variants
const TABLE_DEFAULTS = {
  sortAscIcon: ICONS.chevron_up,
  sortDescIcon: ICONS.chevron_down,
  hover: true,
};
const PAGINATED_TABLE_DEFAULTS = {
  ...TABLE_DEFAULTS,
  density: "compact",
  itemsPerPageOptions: [10, 25, 50, 100, 200],
};

// https://vuetifyjs.com/en/introduction/why-vuetify/#feature-guides
const vuetify = createVuetify({
  locale: {
    locale: "en",
    fallback: "en",
    messages: { cs, en },
  },
  icons: {
    aliases,
    defaultSet: "mdi",
    sets: {
      mdi,
    },
  },
  theme: {
    defaultTheme: DEFAULT_THEME,
    themes: {
      // Vuetify fills tokens a light theme leaves out from the theme named
      // "light", so the tokens marked "pinned" below keep this theme from
      // inheriting the regular light theme's values
      light_high_contrast: {
        dark: false,
        colors: {
          background: "#FCFCFC",
          primary: "#0000BA",
          anchor: "#0000BA", // pinned
          "primary-container": "#B6B6FF",
          "primary-fixed": "#BFBFF9",
          "primary-fixed-dim": "#8C8CEC",
          "on-primary": "#FFFFFF",
          "on-primary-container": "#000000",
          "on-primary-fixed": "#00003E",
          "on-primary-fixed-variant": "#000051",
          secondary: "#66FFF9",
          "secondary-container": "#C8F9F7",
          "secondary-fixed": "#C1F3F1",
          "secondary-fixed-dim": "#9DECE9",
          "on-secondary": "#000000",
          "on-secondary-container": "#000000",
          "on-secondary-fixed": "#002927",
          "on-secondary-fixed-variant": "#005C58",
          tertiary: "#018786",
          "tertiary-container": "#B3F1F0",
          "tertiary-fixed": "#C0EEEE",
          "tertiary-fixed-dim": "#8DD8DB",
          "on-tertiary": "#FFFFFF",
          "on-tertiary-container": "#000000",
          "on-tertiary-fixed": "#001B1B",
          "on-tertiary-fixed-variant": "#002D2D",
          error: "#790000",
          "error-container": "#FFD6D6",
          "on-error": "#FFFFFF",
          "on-error-container": "#000000",
          surface: "#FCFCFC",
          "surface-dim": "#E0E0E0",
          "surface-bright": "#FDFDFD",
          "surface-container-lowest": "#FFFFFF",
          "surface-container-low": "#F8F8F8",
          "surface-container": "#F3F3F3",
          "surface-container-high": "#EDEDED",
          "surface-container-highest": "#E7E7E7",
          "on-surface": "#111111",
          "on-surface-container-high": "#000000", // pinned
          "on-surface-container-highest": "#000000", // pinned
          "on-surface-variant": "#393939",
          "on-surface-container": "#111111",
          "on-surface-container-low": "#111111",
          "inverse-surface": "#2A2A2A",
          "on-inverse-surface": "#F1F1F1",
          "inverse-primary": "#9999FF",
          outline: "#919191",
          "outline-variant": "#D1D1D1",
          shadow: "#000000",
          scrim: "#000000",
          "surface-tint": "#0000BA",
          success: "#055C0F",
          "success-container": "#C8F7CC",
          "on-success": "#FFFFFF",
          "on-success-container": "#000000",
          warning: "#FFB300",
          yellow: LIGHT_THEME_YELLOW,
          "on-warning": "#000000",
          "warning-container": "#FFECB3",
          "on-warning-container": "#000000",
          info: "#2196F3", // pinned
          "on-info": "#FFFFFF", // pinned
          "info-container": "#CFE4FA",
          "on-info-container": "#000000",
          "nav-bar": "#F3F3F3", // same as surface-container
          "on-nav-bar": "#111111",
          counterRegistry: COUNTER_REGISTRY,
        },
        variables: {
          "card-border-radius": "8px",
          // SidePanel logo on the nav-bar: true = white-text logo
          "nav-logo-light": false,
          "card-elevation": 0,
          "card-border": "thin on-surface opacity-100",
          // CelusAlert: status text is mixed toward this color to stay readable
          "alert-text-mix-target": "black",
          "alert-tint-opacity": 0.2,
          // CelusInfoBlock (page tip): bar + icon circle, fill opacity, icon
          // a tint alone does not register in high contrast, so add a border
          "tip-color": "var(--v-theme-primary-container)",
          "tip-bg-opacity": 0.3,
          "tip-icon-color": "var(--v-theme-primary)",
          "tip-border": "2px solid rgb(var(--v-theme-on-surface))",
          // pinned
          "button-border-radius": "2px",
          "nav-active-color": "var(--v-theme-primary-container)",
          "nav-active-text-color": "var(--v-theme-on-primary-container)",
          "nav-active-icon-color": "var(--v-theme-on-primary-container)",
          "tab-active-tint-opacity": 0.08,
        },
      },
      light: {
        dark: false,
        colors: {
          background: "#F5F7F9", // grey page, same as surface
          primary: "#023047",
          // primary is almost black, so links use a mid blue from the same hue (6.0:1)
          anchor: "#0B6A94",
          "primary-container": "#8EDBCE",
          "primary-fixed": "#C1D6E0",
          "primary-fixed-dim": "#8EB4C7",
          "on-primary": "#FFFFFF",
          "on-primary-container": "#000000",
          "on-primary-fixed": "#000000",
          "on-primary-fixed-variant": "#000101",

          // Mint/teal secondary: "active" states (selected toggles, switches, tonal buttons)
          secondary: "#1B7F74", // white label is 4.9:1
          "secondary-container": "#C8EEE7",
          "secondary-fixed": "#D5F2EC",
          "secondary-fixed-dim": "#9FD6CC",
          "on-secondary": "#FFFFFF",
          "on-secondary-container": "#000000",
          "on-secondary-fixed": "#00201D",
          "on-secondary-fixed-variant": "#00332F",

          // Blue tertiary (from blue delight): info, links, steppers
          tertiary: "#0277BD", // white label is 4.8:1
          "tertiary-container": "#BEDCFF",
          "tertiary-fixed": "#C0E4F9",
          "tertiary-fixed-dim": "#8DC9EC",
          "on-tertiary": "#FFFFFF",
          "on-tertiary-container": "#000000",
          "on-tertiary-fixed": "#012941",
          "on-tertiary-fixed-variant": "#013553",

          error: "#ff5252",
          "error-container": "#FFD9DC",
          "on-error": "#FFFFFF",
          "on-error-container": "#000000",

          // page = surface (grey), cards = surface-container (white)
          surface: "#F5F7F9",
          "surface-dim": "#E3E7EB",
          "surface-bright": "#FAFBFC",
          "surface-container-lowest": "#FFFFFF",
          "surface-container-low": "#FFFFFF",
          "surface-container": "#FFFFFF", // cards, tabs, expansion panels
          "surface-container-high": "#F1F4F6", // VSheet, nested panels
          "surface-container-highest": "#E8ECEF",
          "on-surface": "#12161A",
          "on-surface-variant": "#3B4248",
          "on-surface-container": "#12161A",
          "on-surface-container-low": "#12161A",
          "on-surface-container-high": "#12161A",
          "on-surface-container-highest": "#12161A",
          "inverse-surface": "#2A2F34",
          "on-inverse-surface": "#F1F3F5",
          "inverse-primary": "#89ADBF",
          outline: "#868F97", // toggle borders and dividers (3.3:1 on white)
          "outline-variant": "#D3D9DE", // card hairline
          shadow: "#000000",
          scrim: "#000000",
          "surface-tint": "#023047",

          // --- filled in (not part of the palette) ---
          info: "#0B6A94", // same as anchor
          "info-container": "#D6EEF7",
          "on-info": "#FFFFFF",
          "on-info-container": "#000000",
          /* success: "#2E7D32",
          "success-container": "#C8E6C9",
          "on-success": "#FFFFFF",
          "on-success-container": "#000000", */
          success: "#26A14F",
          "on-success": "#FFFFFF",
          "success-container": "#D8EEDF",
          "on-success-container": "#000000",
          "success-text": "#1D7239", // text and counts on light surfaces: 6.0:1 on white
          // Dark gold, so it stays distinct from the orange secondary/tertiary
          warning: LIGHT_THEME_YELLOW,
          "on-warning": "#000000",
          "warning-container": "#FBEBCF",
          "on-warning-container": "#000000",

          yellow: LIGHT_THEME_YELLOW,
          "nav-bar": "#023047", // same as primary
          "on-nav-bar": "#FFFFFF",
          counterRegistry: COUNTER_REGISTRY,
          ...LIGHT_HARVEST_COLORS,
        },
        variables: {
          "card-border-radius": "12px",
          // SidePanel logo on the nav-bar: true = white-text logo
          "nav-logo-light": true,
          "button-border-radius": "8px",
          "card-elevation": 0,
          "card-border": "thin outline-variant opacity-100",
          // CelusAlert: status text is mixed toward this color to stay readable
          "alert-text-mix-target": "black",
          "alert-tint-opacity": 0.15,
          // MenuListItem active item (defaults to primary-container)
          "nav-active-color": "var(--v-theme-primary-container)",
          "nav-active-text-color": "var(--v-theme-on-primary-container)",
          "nav-active-icon-color": "var(--v-theme-primary)",
          // warning icon on the mint active item, mixed toward black (3.5:1, plain is 1.4:1)
          "nav-active-warning-mix": "55%",
          // Active tab wash (overrides.scss reads --v-tab-active-tint-opacity).
          // Lower than before: on a white tab bar a navy wash looks dull at 0.08.
          "tab-active-tint-opacity": 0.05,
          // CelusInfoBlock (page tip): bar + icon circle, fill opacity, icon
          "tip-color": "var(--v-theme-primary-container)",
          "tip-bg-opacity": 0.3,
          "tip-icon-color": "var(--v-theme-primary)",
        },
      },
      dark: {
        dark: true,
        colors: {
          background: "#010101",
          primary: "#90CAF9",
          anchor: "#90CAF9",
          "primary-container": "#0D47A1",
          "primary-fixed": "#C5DDF8",
          "primary-fixed-dim": "#97BEE9",
          "on-primary": "#000000",
          "on-primary-container": "#FFFFFF",
          "on-primary-fixed": "#08274A",
          "on-primary-fixed-variant": "#0A2F5A",
          secondary: "#81D4FA",
          "secondary-container": "#004B73",
          "secondary-fixed": "#C3EBFE",
          "secondary-fixed-dim": "#8ED5F9",
          "on-secondary": "#000000",
          "on-secondary-container": "#FFFFFF",
          "on-secondary-fixed": "#013F5D",
          "on-secondary-fixed-variant": "#014B6F",
          tertiary: "#E1F5FE",
          "tertiary-container": "#1A567D",
          "tertiary-fixed": "#C0E4F9",
          "tertiary-fixed-dim": "#8DC9EC",
          "on-tertiary": "#000000",
          "on-tertiary-container": "#FFFFFF",
          "on-tertiary-fixed": "#012941",
          "on-tertiary-fixed-variant": "#013553",
          error: "#CF6679",
          "error-container": "#B1384E",
          "on-error": "#000000",
          "on-error-container": "#FFFFFF",
          surface: "#080808",
          "surface-dim": "#060606",
          "surface-bright": "#2C2C2C",
          "surface-container-lowest": "#010101",
          "surface-container-low": "#0E0E0E",
          "surface-container": "#151515",
          "surface-container-high": "#1D1D1D",
          "surface-container-highest": "#282828",
          "on-surface": "#F1F1F1",
          "on-surface-variant": "#CACACA",
          "on-surface-container": "#F1F1F1",
          "on-surface-container-low": "#F1F1F1",
          "inverse-surface": "#E8E8E8",
          "on-inverse-surface": "#2A2A2A",
          "inverse-primary": "#445B6B",
          outline: "#777777",
          "outline-variant": "#414141",
          shadow: "#000000",
          scrim: "#000000",
          "surface-tint": "#90CAF9",
          info: "#81D4FA", //same as secondary
          "info-container": "#004B73", // same as secondary-container
          "on-info-container": "#FFFFFF",
          success: "#4CAF50",
          "success-container": "#2E7D32",
          "on-success": "#000000",
          "on-success-container": "#FFFFFF",
          warning: "#FFB962",
          yellow: DARK_THEME_YELLOW,
          "on-warning": "#000000",
          "warning-container": "#663E00",
          "on-warning-container": "#FFFFFF",
          "nav-bar": "#151515", // same as surface-container
          "on-nav-bar": "#F1F1F1",
          counterRegistry: COUNTER_REGISTRY,
          ...DARK_HARVEST_COLORS,
        },
        variables: {
          "card-border-radius": "4px",
          // SidePanel logo on the nav-bar: true = white-text logo
          "nav-logo-light": true,
          "card-elevation": 0,
          // CelusAlert: status text is mixed toward this color to stay readable
          "alert-style": "tint", // CelusAlert: tint instead of container fill
          "alert-text-mix-target": "white",
          "alert-tint-opacity": 0.3,
          // selected tab wash (overrides.scss); light themes use the 0.08 default
          "tab-active-tint-opacity": 0.12,
          // CelusInfoBlock (page tip): bar + icon circle, fill opacity, icon
          "tip-color": "var(--v-theme-primary-container)",
          "tip-bg-opacity": 0.35,
          "tip-icon-color": "var(--v-theme-primary)",
        },
      },
    },
  },
  components: { ...components, VMonthPicker },
  directives,
  defaults: {
    VDataTable: PAGINATED_TABLE_DEFAULTS,
    VDialog: {
      scrollable: true,
    },
    VDataTableServer: PAGINATED_TABLE_DEFAULTS,
    VDataTableVirtual: {
      ...TABLE_DEFAULTS,
      density: "comfortable",
    },
    VAppBar: {
      flat: true,
      // hairline instead of a shadow to separate the bar from the page
      border: "b outline-variant opacity-100",
    },
    VCard: {
      color: "surface-container",
      /* border: "sm outline opacity-30", */
    },
    VExpansionPanel: {
      bgColor: "surface-container",
    },
    VSheet: {
      color: "surface-container-high",
    },
    VTooltip: {
      maxWidth: 450,
    },
    VSkeletonLoader: {
      // take the color of whatever the skeleton sits on (card or page)
      // instead of Vuetify's `surface`, which is the grey page color
      color: "transparent",
    },
    VBtnToggle: {
      density: "compact",
      color: "secondary-container", //activeitem color
      variant: "flat",
      border: "sm outline opacity-100",
    },
    VTabs: {
      grow: true,
      color: "primary",
      sliderColor: "primary",
      bgColor: "surface-container",
    },
    VTab: {
      // inactive label and icon
      baseColor: "on-surface-variant",
    },
    VCheckbox: {
      color: "primary",
    },
    VCheckboxBtn: {
      color: "primary",
    },
    VSwitch: {
      color: "secondary",
    },
    VSelect: {
      color: "primary",
      variant: "underlined",
      density: "compact",
    },
    VTextField: {
      variant: "underlined",
      color: "primary",
      density: "compact",
    },
    VTextarea: {
      variant: "filled",
      bgColor: "surface-container-high",
      color: "primary",
      density: "compact",
    },
    VAutocomplete: {
      variant: "underlined",
      color: "primary",
      density: "compact",
    },
    VDateInput: {
      color: "primary",
      pickerProps: { color: "primary" },
      prependIcon: "",
      prependInnerIcon: ICONS.calendar,
      variant: "underlined",
      density: "compact",
    },
  },
});

// a stale name (e.g. a removed theme) is ignored by Vuetify with a console warning
const startupTheme = rememberedTheme();
if (startupTheme) {
  vuetify.theme.change(startupTheme);
}

export default vuetify;
